import { useEffect, useState } from "react";
import { useViewer } from "../../../../state/ViewerProvider";
import type { CameraView, CameraViewsConfig } from "../../../../shared/api-contract";
import { promptDialog } from "@/shared/ui";
import { Icon } from "../../../kit";
import { CompactCheck, CompactNumber, MiniBtn, Row } from "../../inspector/rows";
import { downloadBlob } from "../../../../util/download";

/**
 * Saved camera viewpoints + tour / turntable (v15). Capture the current pose,
 * fly to a saved one, tour them all, or spin the turntable. Views live in the
 * viewer for the session (not persisted host-side yet).
 */
export function CameraViewsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [views, setViews] = useState<CameraView[]>([]);
  const [touring, setTouring] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [recording, setRecording] = useState(false);
  const [cfg, setCfg] = useState<CameraViewsConfig | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Every mutating RPC below used to be fired with a bare `void` / `.finally()`,
  // so a rejection (recordAllCameraViews throws outright on an empty view list)
  // escaped as an unhandled rejection and the panel looked exactly as it does on
  // success. Route them all through this so the failure is visible.
  const fail = (what: string) => (e: unknown) =>
    setErr(`${what} failed: ${e instanceof Error ? e.message : String(e)}`);

  const refresh = () => { r.listCameraViews().then(setViews).catch(() => {}); };
  useEffect(() => { refresh(); r.getCameraViewsConfig().then(setCfg).catch(() => {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [client]);
  const cset = (patch: Partial<CameraViewsConfig>) => { setErr(null); void r.setCameraViewsConfig(patch).then(setCfg).catch(fail("Config update")); };

  const add = () => { setErr(null); void r.addCameraView().then(refresh).catch(fail("Capture")); };
  const del = (id: string) => { setErr(null); void r.deleteCameraView(id).then(refresh).catch(fail("Delete")); };
  const fly = (id: string) => { setErr(null); void r.flyToCameraView(id, 700).catch(fail("Fly to view")); };
  const rename = async (v: CameraView) => {
    const name = await promptDialog({ title: "View name", label: "Name", defaultValue: v.name });
    if (name == null) return;
    setErr(null);
    void r.renameCameraView(v.id, name).then(refresh).catch(fail("Rename"));
  };
  const tour = () => { setErr(null); setTouring(true); void r.animateAllCameraViews(1400).catch(fail("Tour")).finally(() => setTouring(false)); };
  const stopTour = () => { void r.stopCameraTour().catch(fail("Stop tour")); setTouring(false); };
  const turntable = () => { setErr(null); setSpinning(true); void r.playTurntable({ revolutions: 1, durationMs: 6000 }).catch(fail("Turntable")).finally(() => setSpinning(false)); };
  const stopSpin = () => { void r.stopTurntable().catch(fail("Stop turntable")); setSpinning(false); };

  // Both of these operate on the saved-view list: the viewer's recorder throws
  // "No saved camera views to record" and animateAll silently returns when the
  // list is empty, so they are disabled until there is something to play back.
  const noViews = views.length === 0;

  return (
    <div className="flex flex-col gap-1.5 px-3 py-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <MiniBtn primary onClick={add} icon="add_a_photo">Capture view</MiniBtn>
        {touring
          ? <MiniBtn onClick={stopTour} icon="stop">Stop tour</MiniBtn>
          : <MiniBtn onClick={tour} icon="play_arrow" disabled={noViews}>Tour all</MiniBtn>}
        {spinning
          ? <MiniBtn onClick={stopSpin} icon="stop">Stop spin</MiniBtn>
          : <MiniBtn onClick={turntable} icon="360">Turntable</MiniBtn>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <MiniBtn onClick={() => { setErr(null); void r.focusPreviousCameraView().catch(fail("Previous view")); }} icon="chevron_left">Prev</MiniBtn>
        <MiniBtn onClick={() => { setErr(null); void r.focusNextCameraView().catch(fail("Next view")); }} icon="chevron_right">Next</MiniBtn>
        <MiniBtn
          onClick={() => {
            setErr(null);
            setRecording(true);
            void r
              .recordAllCameraViews()
              .then((blob) => downloadBlob(blob, "camera-views-tour.webm"))
              .catch(fail("Recording"))
              .finally(() => setRecording(false));
          }}
          icon="movie"
          busy={recording}
          disabled={noViews}
        >
          {recording ? "Recording…" : "Record All"}
        </MiniBtn>
      </div>
      {cfg ? (
        <>
          <Row label="Loop All Views"><CompactCheck checked={cfg.viewLooping} onChange={(v) => cset({ viewLooping: v })} /></Row>
          <Row label="View Pause (ms)"><CompactNumber value={cfg.viewPauseTime} width={72} onChange={(v) => cset({ viewPauseTime: v })} /></Row>
          <Row label="Duration (ms)"><CompactNumber value={cfg.animDuration} width={72} onChange={(v) => cset({ animDuration: v })} /></Row>
          <Row label="Rotation Offset"><CompactNumber value={cfg.rotationOffset} step={0.01} width={72} onChange={(v) => cset({ rotationOffset: v })} /></Row>
        </>
      ) : null}
      {err ? <span className="text-[11px] text-ed-red">{err}</span> : null}
      {noViews ? (
        <span className="text-[11px] text-ed-text-3">No saved views. Frame the model, then “Capture view” — “Tour all” and “Record All” need at least one.</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {views.map((v, i) => (
            <li key={v.id} className="flex items-center gap-1.5 rounded border border-ed-border bg-white px-2 py-1">
              <button className="flex min-w-0 flex-1 items-center gap-1.5 text-left" onClick={() => fly(v.id)} title="Fly to view">
                <Icon name="videocam" size={14} className="shrink-0 text-ed-text-3" />
                <span className="truncate text-[12px] text-ed-text">{i + 1}. {v.name}</span>
              </button>
              <button className="shrink-0 text-ed-text-3 hover:text-ed-accent" onClick={() => void rename(v)} title="Rename"><Icon name="edit" size={14} /></button>
              <button className="shrink-0 text-ed-text-3 hover:text-red-500" onClick={() => del(v.id)} title="Delete"><Icon name="close" size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
